// Logique partagée des filtres dashboard : conversion preset → bornes ISO,
// sérialisation/désérialisation depuis l'URL.

export type PeriodPreset = 'mois' | 'trimestre' | 'depuis_janvier' | 'perso';

export interface DashboardFiltersState {
  preset: PeriodPreset;
  // Utilisés uniquement si preset = 'perso' (sinon recalculés à partir du preset).
  custom_from: string; // "YYYY-MM-DD"
  custom_to: string;
  user_id: number | undefined; // undefined = "Tous"
}

export const DEFAULT_FILTERS: DashboardFiltersState = {
  preset: 'depuis_janvier',
  custom_from: '',
  custom_to: '',
  user_id: undefined,
};

// Calcule les bornes ISO UTC inclusives à partir du preset (sauf 'perso' → utilise les dates saisies).
export function presetToBounds(
  filters: DashboardFiltersState,
  now: Date = new Date(),
): { period_from: string; period_to: string } {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + 1; // 1..12
  const day = now.getUTCDate();
  const pad = (n: number): string => String(n).padStart(2, '0');
  const todayEnd = `${String(year)}-${pad(month)}-${pad(day)}T23:59:59.999Z`;

  switch (filters.preset) {
    case 'mois': {
      const from = `${String(year)}-${pad(month)}-01T00:00:00.000Z`;
      return { period_from: from, period_to: todayEnd };
    }
    case 'trimestre': {
      // Trimestre civil : Q1=1-3, Q2=4-6, Q3=7-9, Q4=10-12.
      const quarterStartMonth = month - ((month - 1) % 3); // 1, 4, 7 ou 10
      const from = `${String(year)}-${pad(quarterStartMonth)}-01T00:00:00.000Z`;
      return { period_from: from, period_to: todayEnd };
    }
    case 'depuis_janvier': {
      return { period_from: `${String(year)}-01-01T00:00:00.000Z`, period_to: todayEnd };
    }
    case 'perso': {
      const from =
        filters.custom_from !== ''
          ? `${filters.custom_from}T00:00:00.000Z`
          : `${String(year)}-01-01T00:00:00.000Z`;
      const to = filters.custom_to !== '' ? `${filters.custom_to}T23:59:59.999Z` : todayEnd;
      return { period_from: from, period_to: to };
    }
  }
}

// Libellé court pour affichage (ex: "Depuis le 1er janvier 2026").
export function presetLabel(filters: DashboardFiltersState, now: Date = new Date()): string {
  const year = now.getUTCFullYear();
  switch (filters.preset) {
    case 'mois':
      return `Mois en cours`;
    case 'trimestre': {
      const month = now.getUTCMonth() + 1;
      const q = Math.ceil(month / 3);
      return `Trimestre en cours (T${String(q)} ${String(year)})`;
    }
    case 'depuis_janvier':
      return `Depuis le 1er janvier ${String(year)}`;
    case 'perso':
      return `Période personnalisée`;
  }
}

// ── URL ↔ state ───────────────────────────────────────────────────────
const PRESETS: PeriodPreset[] = ['mois', 'trimestre', 'depuis_janvier', 'perso'];

export function filtersFromSearchParams(sp: URLSearchParams): DashboardFiltersState {
  const presetRaw = sp.get('preset');
  const preset: PeriodPreset = PRESETS.includes(presetRaw as PeriodPreset)
    ? (presetRaw as PeriodPreset)
    : 'depuis_janvier';
  const userIdRaw = sp.get('user_id');
  const userId = userIdRaw !== null && userIdRaw !== '' ? Number(userIdRaw) : undefined;
  return {
    preset,
    custom_from: sp.get('from') ?? '',
    custom_to: sp.get('to') ?? '',
    user_id: Number.isFinite(userId) && userId !== undefined && userId > 0 ? userId : undefined,
  };
}

export function filtersToSearchParams(filters: DashboardFiltersState): URLSearchParams {
  const sp = new URLSearchParams();
  if (filters.preset !== 'depuis_janvier') sp.set('preset', filters.preset);
  if (filters.preset === 'perso') {
    if (filters.custom_from !== '') sp.set('from', filters.custom_from);
    if (filters.custom_to !== '') sp.set('to', filters.custom_to);
  }
  if (filters.user_id !== undefined) sp.set('user_id', String(filters.user_id));
  return sp;
}
